import React, { useEffect, useState } from 'react';
import {
  API,
  showError,
  showSuccess
} from '../helpers';

import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Header,
  Input,
  Label,
  Message,
  Segment
} from 'semantic-ui-react';

const GitSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editableSettings, setEditableSettings] = useState({
    lfs_enabled: false,
    git_max_connections: 100,
    git_max_timeout: 300,
    git_idle_timeout: 600
  });

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/git/settings');
      const { success, message, data } = res.data;
      if (success) {
        setSettings(data);
        setEditableSettings({
          lfs_enabled: data.lfs?.enabled || false,
          git_max_connections: data.git?.max_connections || 100,
          git_max_timeout: data.git?.max_timeout || 300,
          git_idle_timeout: data.git?.idle_timeout || 600
        });
      } else {
        showError(message);
      }
    } catch (error) {
      showError('加载设置失败');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await API.put('/api/admin/git/settings', editableSettings);
      const { success, message, restart_required, note } = res.data;
      if (success) {
        showSuccess(message);
        if (restart_required) {
          showError('部分设置需要重启服务才能生效');
        }
        if (note) {
          showError(note);
        }
        await loadSettings();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('保存设置失败');
    }
    setSaving(false);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  if (loading) {
    return (
      <Header as='h3'>
        加载中...
      </Header>
    );
  }

  return (
    <>
      <Header as='h3'>
        Git系统设置
        <Header.Subheader>配置Git服务的运行参数</Header.Subheader>
      </Header>

      <Message warning>
        <Message.Header>注意事项</Message.Header>
        <ul>
          <li>只能修改部分动态配置参数</li>
          <li>某些设置可能需要重启服务才能生效</li>
          <li>请谨慎修改，错误的配置可能导致服务不可用</li>
        </ul>
      </Message>

      {settings && (
        <>
          <Segment>
            <Header as='h4'>基本信息（只读）</Header>
            <Card.Group>
              <Card>
                <Card.Content>
                  <Card.Header>服务名称</Card.Header>
                  <Card.Description>{settings.name}</Card.Description>
                </Card.Content>
              </Card>
              <Card>
                <Card.Content>
                  <Card.Header>数据路径</Card.Header>
                  <Card.Description>{settings.data_path}</Card.Description>
                </Card.Content>
              </Card>
            </Card.Group>
          </Segment>

          <Segment>
            <Header as='h4'>SSH 配置（只读）</Header>
            <Card.Group>
              <Card>
                <Card.Content>
                  <Card.Header>启用状态</Card.Header>
                  <Card.Description>
                    <Label color={settings.ssh?.enabled ? 'green' : 'red'}>
                      {settings.ssh?.enabled ? '已启用' : '已禁用'}
                    </Label>
                  </Card.Description>
                </Card.Content>
              </Card>
              <Card>
                <Card.Content>
                  <Card.Header>监听地址</Card.Header>
                  <Card.Description>{settings.ssh?.listen_addr}</Card.Description>
                </Card.Content>
              </Card>
              <Card>
                <Card.Content>
                  <Card.Header>公开URL</Card.Header>
                  <Card.Description>{settings.ssh?.public_url}</Card.Description>
                </Card.Content>
              </Card>
            </Card.Group>
          </Segment>

          <Segment>
            <Header as='h4'>Git 协议配置</Header>
            <Card.Group>
              <Card>
                <Card.Content>
                  <Card.Header>启用状态（只读）</Card.Header>
                  <Card.Description>
                    <Label color={settings.git?.enabled ? 'green' : 'red'}>
                      {settings.git?.enabled ? '已启用' : '已禁用'}
                    </Label>
                  </Card.Description>
                </Card.Content>
              </Card>
              <Card>
                <Card.Content>
                  <Card.Header>监听地址（只读）</Card.Header>
                  <Card.Description>{settings.git?.listen_addr}</Card.Description>
                </Card.Content>
              </Card>
            </Card.Group>

            <Divider />
            
            <Form>
              <Form.Field>
                <label>最大连接数</label>
                <Input
                  type='number'
                  value={editableSettings.git_max_connections}
                  onChange={(e, { value }) => {
                    setEditableSettings({
                      ...editableSettings,
                      git_max_connections: parseInt(value) || 100
                    });
                  }}
                />
              </Form.Field>
              <Form.Field>
                <label>最大超时时间（秒）</label>
                <Input
                  type='number'
                  value={editableSettings.git_max_timeout}
                  onChange={(e, { value }) => {
                    setEditableSettings({
                      ...editableSettings,
                      git_max_timeout: parseInt(value) || 300
                    });
                  }}
                />
              </Form.Field>
              <Form.Field>
                <label>空闲超时时间（秒）</label>
                <Input
                  type='number'
                  value={editableSettings.git_idle_timeout}
                  onChange={(e, { value }) => {
                    setEditableSettings({
                      ...editableSettings,
                      git_idle_timeout: parseInt(value) || 600
                    });
                  }}
                />
              </Form.Field>
            </Form>
          </Segment>

          <Segment>
            <Header as='h4'>HTTP 配置（只读）</Header>
            <Card.Group>
              <Card>
                <Card.Content>
                  <Card.Header>启用状态</Card.Header>
                  <Card.Description>
                    <Label color={settings.http?.enabled ? 'green' : 'red'}>
                      {settings.http?.enabled ? '已启用' : '已禁用'}
                    </Label>
                  </Card.Description>
                </Card.Content>
              </Card>
              <Card>
                <Card.Content>
                  <Card.Header>监听地址</Card.Header>
                  <Card.Description>{settings.http?.listen_addr}</Card.Description>
                </Card.Content>
              </Card>
              <Card>
                <Card.Content>
                  <Card.Header>公开URL</Card.Header>
                  <Card.Description>{settings.http?.public_url}</Card.Description>
                </Card.Content>
              </Card>
            </Card.Group>
          </Segment>

          <Segment>
            <Header as='h4'>LFS 配置</Header>
            <Form>
              <Form.Field>
                <Checkbox
                  checked={editableSettings.lfs_enabled}
                  label='启用 Git LFS'
                  onChange={(e, { checked }) => {
                    setEditableSettings({
                      ...editableSettings,
                      lfs_enabled: checked
                    });
                  }}
                />
              </Form.Field>
              <div style={{ marginTop: '10px' }}>
                <Label color={settings.lfs?.ssh_enabled ? 'green' : 'grey'} size='small'>
                  SSH LFS: {settings.lfs?.ssh_enabled ? '已启用' : '已禁用'}
                </Label>
              </div>
            </Form>
          </Segment>

          <Divider />

          <Button 
            positive 
            loading={saving} 
            onClick={saveSettings}
            size='large'
          >
            保存设置
          </Button>
        </>
      )}
    </>
  );
};

export default GitSettings; 